import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

async function getOrgId() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: org } = await supabase
    .from('organizations')
    .select('id')
    .eq('user_id', user.id)
    .single();
  return org?.id || null;
}

export function useMetrics() {
  const [metrics, setMetrics] = useState({
    messagesTotal: 0, ordersPending: 0, escalades: 0, revenue: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMetrics() {
      try {
        const ORG_ID = await getOrgId();
        if (!ORG_ID) { setLoading(false); return; }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const { count: messagesCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', ORG_ID)
          .eq('sender', 'ai')
          .gte('created_at', today.toISOString());

        const { count: pendingOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', ORG_ID)
          .eq('status', 'pending');

        const { count: escaladeCount } = await supabase
          .from('conversation_states')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', ORG_ID)
          .eq('current_state', 'escalate');

        const firstDay = new Date();
        firstDay.setDate(1);
        firstDay.setHours(0, 0, 0, 0);

        const { data: orders } = await supabase
          .from('orders')
          .select('amount')
          .eq('organization_id', ORG_ID)
          .eq('status', 'confirmed')
          .gte('created_at', firstDay.toISOString());

        const revenue = orders?.reduce((sum, o) => sum + (o.amount || 0), 0) || 0;

        setMetrics({
          messagesTotal: messagesCount || 0,
          ordersPending: pendingOrders || 0,
          escalades: escaladeCount || 0,
          revenue
        });
      } catch (error) {
        console.error('Erreur métriques:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, []);

  return { metrics, loading };
}

export function useConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchConversations() {
      try {
        const ORG_ID = await getOrgId();
        if (!ORG_ID) { setLoading(false); return; }

        const { data } = await supabase
          .from('conversations')
          .select(`*, contacts(*), messages(content, sender, created_at), conversation_states(current_state)`)
          .eq('organization_id', ORG_ID)
          .order('created_at', { ascending: false })
          .limit(20);

        setConversations(data || []);
      } catch (error) {
        console.error('Erreur conversations:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchConversations();

    const subscription = supabase
      .channel('messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => fetchConversations())
      .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  return { conversations, loading };
}

export function useOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchOrders() {
      try {
        const ORG_ID = await getOrgId();
        if (!ORG_ID) { setLoading(false); return; }

        const { data } = await supabase
          .from('orders')
          .select(`*, contacts(*)`)
          .eq('organization_id', ORG_ID)
          .order('created_at', { ascending: false });

        setOrders(data || []);
      } catch (error) {
        console.error('Erreur commandes:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchOrders();
  }, []);

  async function confirmOrder(orderId) {
    await supabase
      .from('orders')
      .update({ status: 'confirmed', confirmed_by_owner: true, confirmed_at: new Date() })
      .eq('id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'confirmed' } : o));
  }

  async function rejectOrder(orderId) {
    await supabase
      .from('orders')
      .update({ status: 'rejected' })
      .eq('id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'rejected' } : o));
  }

  return { orders, loading, confirmOrder, rejectOrder };
}

export function useContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchContacts() {
      try {
        const ORG_ID = await getOrgId();
        if (!ORG_ID) { setLoading(false); return; }

        const { data } = await supabase
          .from('contacts')
          .select('*')
          .eq('organization_id', ORG_ID)
          .order('last_interaction', { ascending: false });

        setContacts(data || []);
      } catch (error) {
        console.error('Erreur contacts:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchContacts();
  }, []);

  return { contacts, loading };
}

export function useMessages() {
  const [whatsappConvs, setWhatsappConvs] = useState([]);
  const [instagramConvs, setInstagramConvs] = useState([]);
  const [facebookConvs, setFacebookConvs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMessages() {
      try {
        const ORG_ID = await getOrgId();
        if (!ORG_ID) { setLoading(false); return; }

        const { data } = await supabase
          .from('conversations')
          .select(`*, contacts(*), messages(content, sender, created_at), conversation_states(current_state)`)
          .eq('organization_id', ORG_ID)
          .order('created_at', { ascending: false });

        const all = data || [];
        setWhatsappConvs(all.filter(c => c.channel === 'whatsapp'));
        setInstagramConvs(all.filter(c => c.channel === 'instagram'));
        setFacebookConvs(all.filter(c => c.channel === 'facebook'));
      } catch (error) {
        console.error('Erreur messages:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchMessages();

    const subscription = supabase
      .channel('conversations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => fetchMessages())
      .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  return { whatsappConvs, instagramConvs, facebookConvs, loading };
}

export function useMessageStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const ORG_ID = await getOrgId();
        if (!ORG_ID) { setLoading(false); return; }

        const { data } = await supabase
          .from('organizations')
          .select('messages_used, messages_limit, messages_reset_date, plan_name, is_trial, trial_expires_at, trial_messages_used, trial_messages_limit')
          .eq('id', ORG_ID)
          .single();

        if (!data) { setLoading(false); return; }

        const now = new Date();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const daysRemaining = lastDay.getDate() - now.getDate();

        if (data.is_trial) {
          const expires = new Date(data.trial_expires_at);
          const hoursLeft = Math.max(0, Math.round((expires - now) / 3600000));
          const used = data.trial_messages_used || 0;
          const limit = data.trial_messages_limit || 100;
          const remaining = limit - used;
          const percentage = Math.round((used / limit) * 100);

          setStats({ used, limit, remaining, percentage, daysRemaining, planName: 'trial', isTrial: true, hoursLeft });
        } else {
          const used = data.messages_used || 0;
          const limit = data.messages_limit || 3600;
          const remaining = limit - used;
          const percentage = Math.round((used / limit) * 100);

          setStats({ used, limit, remaining, percentage, daysRemaining, planName: data.plan_name, isTrial: false });
        }
      } catch (error) {
        console.error('Erreur stats messages:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();

    const subscription = supabase
      .channel('message-stats')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'organizations' }, () => fetchStats())
      .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  return { stats, loading };
}